import { useState, useCallback, useRef, useMemo } from 'react';

const DEFAULT_FILTER_STATE = {
    paid: true,
    secondee: true,
    consultant: true,
    volunteer: true,
};

/**
 * Map from position type enum values to filter state keys.
 */
const TYPE_TO_FILTER_KEY = {
    P: 'paid',
    S: 'secondee',
    V: 'volunteer',
    C: 'consultant',
};

/**
 * Determine whether a node is visible given the current filter state.
 * Stub nodes are always kept (they replace hidden groups).
 * Nodes with an unrecognised/null type are always shown.
 */
function isNodeVisible(node, filterState) {
    if (node._isStub) return true;
    const key = TYPE_TO_FILTER_KEY[node.type];
    if (!key) return true; // unrecognised or null type — always visible
    return filterState[key] !== false;
}

/**
 * Apply the current filter to a flat OrgChart node array.
 *
 * Strategy:
 *   1. Collect the set of hidden node IDs.
 *   2. For each hidden node, re-parent its children to the hidden node's pid (grandparent).
 *   3. Accumulate a count of hidden nodes per parent pid.
 *   4. For each parent with hidden children, inject a single stub node.
 *
 * @param {Array}  nodes       - Flat array of position node objects.
 * @param {Object} filterState - { paid, secondee, consultant, volunteer } booleans.
 * @returns {Array} Filtered flat array suitable for OrgChart.
 */
export function applyFilter(nodes, filterState) {
    // All types visible — short-circuit
    const allVisible = Object.values(filterState).every(Boolean);
    if (allVisible) return nodes;

    // Build a map of id → node for quick lookups
    const nodeMap = {};
    nodes.forEach((n) => {
        nodeMap[n.id] = n;
    });

    // First pass: determine which nodes are hidden
    const hiddenIds = new Set(nodes.filter((n) => !isNodeVisible(n, filterState)).map((n) => n.id));

    // Resolve effective pid for a node, skipping hidden ancestors
    function effectivePid(pid) {
        if (pid === '' || pid === null || pid === undefined) return pid;
        if (!hiddenIds.has(pid)) return pid;
        const ancestor = nodeMap[pid];
        return ancestor ? effectivePid(ancestor.pid) : pid;
    }

    const result = [];
    // stubsPerPid: effective_pid → { count }
    const stubsPerPid = {};

    nodes.forEach((node) => {
        if (hiddenIds.has(node.id)) {
            // Tally hidden node under its effective parent
            const ePid = effectivePid(node.pid);
            const key = ePid === '' || ePid == null ? '__root__' : ePid;
            if (!stubsPerPid[key]) {
                stubsPerPid[key] = { ePid, count: 0 };
            }
            stubsPerPid[key].count += 1;
            return; // skip this node
        }

        // Reparent visible node if its direct parent is hidden
        const rePid = effectivePid(node.pid);
        if (rePid !== node.pid) {
            result.push({ ...node, pid: rePid });
        } else {
            result.push(node);
        }
    });

    // Inject stub nodes for each parent bucket
    Object.entries(stubsPerPid).forEach(([, { ePid, count }]) => {
        const stubPid = ePid === '__root__' ? '' : ePid;
        result.push({
            id: `stub-${stubPid ?? 'root'}`,
            pid: stubPid,
            name: count === 1 ? $t('1 hidden role') : $t('{{count}} hidden roles', { count }),
            type: null,
            dates: '',
            html: '',
            tags: ['stub'],
            _isStub: true,
        });
    });

    return result;
}

/**
 * Persist the current filter state to the server.
 *
 * @param {Object} filterState  - The new filter state to persist.
 * @param {string} preferenceKey - e.g. "dept_42" or "venue_42"
 * @param {Object} existingValue - Full existing preference value object.
 */
async function persistFilterState(filterState, preferenceKey, existingValue) {
    const newValue = { ...existingValue, [preferenceKey]: filterState };
    try {
        await fetch('/preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                Accept: 'application/json',
            },
            body: JSON.stringify({ module: 'organigram_filter', value: newValue }),
        });
    } catch (e) {
        // Non-blocking — preference save failures should not break the chart
        console.warn('[useOrganigramFilter] Failed to persist filter state', e);
    }
}

/**
 * useOrganigramFilter
 *
 * Manages organigram type filter state and persistence.
 *
 * @param {Object} options
 * @param {Object} options.initialFilterState   - Filter state loaded from server preference (for this chart key).
 * @param {Object} options.fullPreferenceValue  - Full preference value object (all chart keys).
 * @param {string} options.preferenceKey        - e.g. "dept_42" or "venue_42"
 * @param {Array}  options.nodes                - Raw node array from the API.
 *
 * @returns {{ filterState, setFilterState, filteredNodes }}
 */
export default function useOrganigramFilter({ initialFilterState = DEFAULT_FILTER_STATE, fullPreferenceValue = {}, preferenceKey, nodes = [] }) {
    const [filterState, setFilterStateRaw] = useState({
        ...DEFAULT_FILTER_STATE,
        ...initialFilterState,
    });

    // Track the full preference value in a ref so the persist callback always
    // uses the latest version (avoids stale closure overwriting sibling chart keys).
    const fullPreferenceRef = useRef(fullPreferenceValue);

    const setFilterState = useCallback(
        (newState) => {
            setFilterStateRaw(newState);
            // Merge the new filter state into the tracked full preference object
            fullPreferenceRef.current = { ...fullPreferenceRef.current, [preferenceKey]: newState };
            persistFilterState(newState, preferenceKey, fullPreferenceRef.current);
        },
        [preferenceKey],
    );

    const filteredNodes = useMemo(() => applyFilter(nodes, filterState), [nodes, filterState]);

    return { filterState, setFilterState, filteredNodes };
}
